import axios from 'axios';
import { getApiBaseUrl, resolveAbsoluteApiPath } from '../utils/apiOrigin';

const API_BASE_URL = getApiBaseUrl();

const chatApi = axios.create({
  baseURL: `${API_BASE_URL}/chat`,
  timeout: 30000,
});

chatApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('student_token');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['ngrok-skip-browser-warning'] = '1';
  return config;
});

export interface ChatAttachment {
  url: string;
  filename: string;
  mimetype: string;
  size?: number;
}

export interface ChatMessage {
  _id: string;
  conversationId: string;
  senderRole: 'student' | 'admin' | 'system';
  sender?: string;
  content: string;
  attachments: ChatAttachment[];
  readByAdmin: boolean;
  readByStudent: boolean;
  createdAt: string;
}

export interface ChatConversation {
  _id: string;
  student?: unknown;
  course?: string;
  status: 'open' | 'closed' | 'pending';
  context?: {
    courseId?: string;
    courseTitle?: Record<string, string>;
    item?: {
      chapterId?: string;
      itemId?: string;
      itemType?: string;
      itemTitle?: Record<string, string>;
      path?: string;
    };
    updatedAt?: string;
  };
  lastMessageAt?: string;
  lastMessagePreview?: string;
  unreadByAdmin: number;
  unreadByStudent: number;
}

export const resolveChatMediaUrl = (path: string): string => resolveAbsoluteApiPath(path);

export const chatAPI = {
  getConversation: async (uiLanguage?: string) => {
    const { data } = await chatApi.get<{ conversation: ChatConversation; messages: ChatMessage[] }>(
      '/my',
      { params: uiLanguage ? { lang: uiLanguage } : undefined }
    );
    return data;
  },

  getUnreadCount: async (uiLanguage?: string) => {
    const { data } = await chatApi.get<{ unreadCount: number }>('/my/unread-count', {
      params: uiLanguage ? { lang: uiLanguage } : undefined,
    });
    return data.unreadCount;
  },

  updateContext: async (payload: {
    courseId?: string;
    courseTitle?: Record<string, string>;
    uiLanguage?: string;
    item?: ChatConversation['context'] extends infer C ? C extends { item?: infer I } ? I : never : never;
  }) => {
    await chatApi.patch('/my/context', payload);
  },

  sendMessage: async (
    content: string,
    attachments: ChatAttachment[] = [],
    context?: {
      courseId?: string;
      item?: {
        chapterId?: string;
        itemId?: string;
        itemType?: string;
        path?: string;
      };
    },
    uiLanguage?: string
  ) => {
    const { data } = await chatApi.post<{ message: ChatMessage; conversation: ChatConversation }>(
      '/my/messages',
      { content, attachments, courseId: context?.courseId, item: context?.item, uiLanguage }
    );
    return data;
  },

  markRead: async (conversationId: string) => {
    await chatApi.post(`/conversations/${conversationId}/read`);
  },

  uploadImage: async (file: File): Promise<ChatAttachment> => {
    const formData = new FormData();
    formData.append('image', file);
    const token = localStorage.getItem('student_token');
    const { data } = await axios.post(`${API_BASE_URL}/images/single`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
        'ngrok-skip-browser-warning': '1',
      },
    });
    const image = data.data.image;
    return {
      url: image.url,
      filename: image.filename,
      mimetype: image.mimetype,
      size: image.size,
    };
  },
};

export default chatAPI;
